import type { SeoGeneratedComponentStatus } from './types'

export const SEO_GENERATED_ARTIFACT_QUEUED_STALE_MS = 5 * 60 * 1000
export const SEO_GENERATED_ARTIFACT_GENERATING_STALE_MS = 15 * 60 * 1000

export interface SeoGeneratedArtifactLifecycle {
  status?: SeoGeneratedComponentStatus | null
  generationAttemptId?: string | null
  queuedAt?: string | null
  startedAt?: string | null
  completedAt?: string | null
  generatedAt?: string | null
  lastError?: string | null
}

type LifecycleRecord = object & SeoGeneratedArtifactLifecycle

function toTimestampMs(value: string | Date | null | undefined): number | null {
  if (value == null) return null
  const date = value instanceof Date ? value : new Date(value)
  const time = date.getTime()
  return Number.isFinite(time) ? time : null
}

function toIsoString(value: string | Date | null | undefined): string {
  if (value instanceof Date) return value.toISOString()
  if (typeof value === 'string' && value.trim().length > 0) return value
  return new Date().toISOString()
}

export function isSeoGeneratedArtifactProcessingStatus(
  status: SeoGeneratedComponentStatus | string | null | undefined
): status is 'queued' | 'generating' {
  return status === 'queued' || status === 'generating'
}

export function getSeoGeneratedArtifactProcessingTimestamp(
  artifact: SeoGeneratedArtifactLifecycle | null | undefined
): string | null {
  if (!artifact?.status) return null

  if (artifact.status === 'queued') {
    return (
      artifact.queuedAt ?? artifact.startedAt ?? artifact.generatedAt ?? null
    )
  }

  if (artifact.status === 'generating') {
    return (
      artifact.startedAt ?? artifact.queuedAt ?? artifact.generatedAt ?? null
    )
  }

  return null
}

export function isSeoGeneratedArtifactProcessingStale(args: {
  artifact: SeoGeneratedArtifactLifecycle | null | undefined
  fallbackUpdatedAt?: string | Date | null
  now?: number
  staleWhenProcessingWithoutTimestamp?: boolean
}): boolean {
  const status = args.artifact?.status
  if (!isSeoGeneratedArtifactProcessingStatus(status)) return false

  const lifecycleTimestamp = getSeoGeneratedArtifactProcessingTimestamp(
    args.artifact
  )
  if (!lifecycleTimestamp && args.staleWhenProcessingWithoutTimestamp) {
    return true
  }

  const timestamp = lifecycleTimestamp ?? args.fallbackUpdatedAt ?? null
  if (!timestamp) return Boolean(args.staleWhenProcessingWithoutTimestamp)

  const timestampMs = toTimestampMs(timestamp)
  if (timestampMs == null)
    return Boolean(args.staleWhenProcessingWithoutTimestamp)

  const ageMs = (args.now ?? Date.now()) - timestampMs
  const thresholdMs =
    status === 'queued'
      ? SEO_GENERATED_ARTIFACT_QUEUED_STALE_MS
      : SEO_GENERATED_ARTIFACT_GENERATING_STALE_MS

  return ageMs >= thresholdMs
}

export function getEffectiveSeoGeneratedArtifactStatus(args: {
  artifact: SeoGeneratedArtifactLifecycle | null | undefined
  fallbackUpdatedAt?: string | Date | null
  now?: number
  staleWhenProcessingWithoutTimestamp?: boolean
}): SeoGeneratedComponentStatus | null {
  const status = args.artifact?.status ?? null
  if (!status) return null

  if (
    isSeoGeneratedArtifactProcessingStale({
      artifact: args.artifact,
      fallbackUpdatedAt: args.fallbackUpdatedAt,
      now: args.now,
      staleWhenProcessingWithoutTimestamp:
        args.staleWhenProcessingWithoutTimestamp,
    })
  ) {
    return 'failed'
  }

  return status
}

export function buildQueuedSeoGeneratedArtifact<T extends object>(input: {
  existing?: T | null
  generationAttemptId?: string | null
  now?: string | Date
}): T & LifecycleRecord & { status: 'queued' } {
  const queuedAt = toIsoString(input.now)
  return {
    ...(input.existing ?? ({} as T)),
    status: 'queued',
    generationAttemptId: input.generationAttemptId ?? null,
    queuedAt,
    startedAt: null,
    completedAt: null,
    lastError: null,
  } as T & LifecycleRecord & { status: 'queued' }
}

export function buildGeneratingSeoGeneratedArtifact<T extends object>(input: {
  existing?: T | null
  generationAttemptId?: string | null
  now?: string | Date
}): T & LifecycleRecord & { status: 'generating' } {
  const startedAt = toIsoString(input.now)
  const existingLifecycle = input.existing as
    | SeoGeneratedArtifactLifecycle
    | null
    | undefined
  return {
    ...(input.existing ?? ({} as T)),
    status: 'generating',
    generationAttemptId:
      input.generationAttemptId ??
      existingLifecycle?.generationAttemptId ??
      null,
    queuedAt: existingLifecycle?.queuedAt ?? null,
    startedAt,
    completedAt: null,
    lastError: null,
  } as T & LifecycleRecord & { status: 'generating' }
}

export function buildTerminalSeoGeneratedArtifact<T extends object>(input: {
  existing?: T | null
  status: 'ready' | 'failed'
  now?: string | Date
  lastError?: string | null
}): T & LifecycleRecord & { status: 'ready' | 'failed' } {
  const completedAt = toIsoString(input.now)
  return {
    ...(input.existing ?? ({} as T)),
    status: input.status,
    completedAt,
    lastError: input.status === 'failed' ? (input.lastError ?? null) : null,
  } as T & LifecycleRecord & { status: 'ready' | 'failed' }
}
